import React, {
  MouseEvent,
  useEffect,
  useState,
  KeyboardEvent,
} from 'react';
import clsx from 'clsx';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import {
  SelectOptionProps,
} from 'core/components/Lookup/Generic/DefaultOption';
import ToolTip from 'core/components/Tooltip';
import PinIcon from 'core/icons/pin';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useCreateAlias,
  useDeleteAlias,
  useToggleFavorite,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  ServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import Chip from 'core/components/Chip';
import {
  formatMoney,
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
 DEFAULT_CONNECTOR,
} from 'core/constants';
import compact from 'lodash/compact';

export default function BillingCodeRow({
  option,
  getLabel,
  refetch,
  ...props
}: SelectOptionProps<ServiceCode>) {
  const [showInput, setShowInput] = useState<boolean>(false);
  const [changingFavorite, setChangingFavorite] = useState<boolean>(false);
  const [changingAlias, setChangingAlias] = useState<boolean>(false);
  const [deletingAlias, setDeletingAlias] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const {
    code,
    description,
    alias,
    isFavorited,
    defaultBilledAmountPerUnit,
    isRTMEligible,
    isCCMEligible,
    inclusionTerms,
  } = option;

  const { mutateAsync: toggleFavorite } = useToggleFavorite();
  const { mutateAsync: createAlias } = useCreateAlias();
  const { mutateAsync: deleteAlias } = useDeleteAlias();

  useEffect(() => {
    setKeyword(alias ?? '');
  }, [alias]);

  const handleCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowInput(false);
    setKeyword('');
  };

  const handleCreateAlias = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setChangingAlias(true);
    setShowInput(false);
    await createAlias({ code, alias: keyword ?? '' });
    await refetch?.();
    setChangingAlias(false);
  };

  const handleToggleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setChangingFavorite(true);
    await toggleFavorite({ code });
    // @todo emit favorite changed!
    await refetch?.();
    setChangingFavorite(false);
  };

  const handleDeleteAlias = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDeletingAlias(true);
    await deleteAlias({ code, alias });
    await refetch?.();
    setDeletingAlias(false);
  };

  const handleAddKeyword = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setShowInput(!showInput);
  };

  const eligibleState = compact([
    isRTMEligible ? 'RTM' : null,
    isCCMEligible ? 'CCM' : null,
  ]).join(' and ');

  return (
    <div
      {...props}
      className={clsx(props.className, 'w-full truncate !block border-b border-gray-100 pb-2')}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-row items-center justify-between w-full">
          <ToolTip
            popperClassName="billing-code-content"
            placement="top-start"
            title={(
              <div className="flex gap-1">
                <span className="block text-xs font-medium">
                  {`${code}${DEFAULT_CONNECTOR}${description}`}
                </span>
                <span className="block text-xs pr-1">{`${notEmptyString(inclusionTerms) ? ` • ${inclusionTerms}` : ''}`}</span>
              </div>
            )}
          >
            <div className="flex items-center min-w-0 overflow-hidden">
              <IconButton
                disabled={changingFavorite}
                className="z-[10] !p-0.5"
                color="info"
                onClick={handleToggleFavorite}
              >
                {changingFavorite && (
                  <Spinner size={16} className="!text-gray-400 !bg-transparent" />
                )}
                {!changingFavorite && (
                  <PinIcon
                    className={clsx('!text-base text-gray-500', {
                      '!text-blue-500': isFavorited,
                    })}
                  />
                )}
              </IconButton>
              <span className={clsx('text-xs ml-2 truncate', { 'font-semibold': showInput })}>
                <strong>{code}</strong>
                {DEFAULT_CONNECTOR}
                {description}
              </span>
            </div>
          </ToolTip>
          <span className="text-xs !text-right font-semibold text-gray-700 whitespace-nowrap ml-2 !min-w-[50px]">
            {!isNil(defaultBilledAmountPerUnit) ? formatMoney(defaultBilledAmountPerUnit) : ''}
          </span>
        </div>

        <div className="flex flex-row items-center justify-between w-full mt-2 pl-7">
          <span className="text-2xs truncate flex items-center min-w-0 flex-1 overflow-hidden">
            {(isRTMEligible || isCCMEligible) && (
              <span className="inline-flex items-center gap-1 shrink-0 font-semibold text-gray-500">
                <CheckCircleOutlinedIcon className="!w-3.5 !h-3.5" />
                Eligible for {eligibleState}
              </span>
            )}
            {isRTMEligible && notEmptyString(inclusionTerms) && (
              <span className="text-gray-500 px-1">•</span>
            )}
            {notEmptyString(inclusionTerms) && inclusionTerms}
          </span>
          <div className="flex items-center shrink-0 ml-2">
            {isEmptyString(keyword) && (
              <Button
                className={clsx('!w-4 !h-4 !rounded-full !transition-transform duration-100', { '!rotate-45': showInput })}
                variant="text"
                onClick={handleAddKeyword}
              >
                <AddIcon
                  className={`${showInput ? 'text-secondary' : 'text-gray-400'} font-normal`}
                  fontSize="small"
                />
              </Button>
            )}
            {(changingAlias && !deletingAlias) && (
              <Spinner size={16} className="!text-gray-400 !bg-white ml-2" />
            )}
            {!showInput && alias && !changingAlias && !deletingAlias && (
              <Chip
                classes={{
                  root: '!w-fit',
                  text: 'ml-2 !max-w-[7.5rem]',
                  chip: '!p-1 !max-h-6',
                }}
                value={alias}
                onClose={handleDeleteAlias}
              />
            )}
          </div>
        </div>
      </div>
      {showInput && (
        <div className="mt-4">
          <Input
            className="space-y-1"
            inputClasses="text-xs font-normal text-gray-500"
            labelClasses="text-xs font-medium text-gray-400"
            label="Create Additional Keyword"
            id="additionalKeyword"
            value={keyword ?? ''}
            onChange={setKeyword}
            onClick={(e: MouseEvent<HTMLInputElement>) => {
              e.stopPropagation();
              (e.target as HTMLInputElement)?.focus();
            }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { e.stopPropagation(); }}
          />
          <div
            className="flex justify-end items-end my-2.5 space-x-2.5"
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
            >
              Close
            </Button>
            <Button
              disabled={isEmptyString(keyword)}
              variant="contained"
              color="primary"
              onClick={handleCreateAlias}
            >
              Create
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
